/**
 * useChat — Custom hook for managing chat state with the OMS agent.
 *
 * Handles message history, loading state, error handling, and persistence
 * to sessionStorage so messages survive page navigation but clear on tab close.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import * as chatApi from '../api/chatApi';

const STORAGE_KEY = 'oms-chat-messages';

/**
 * Load persisted messages from sessionStorage.
 * Returns empty array if nothing is stored or parsing fails.
 */
function loadMessages() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    return JSON.parse(raw);
  } catch {
    return [];
  }
}

/**
 * Persist messages to sessionStorage.
 */
function saveMessages(messages) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
  } catch {
    // sessionStorage full or unavailable — silently ignore
  }
}

/**
 * useChat hook — manages the full chat lifecycle.
 *
 * @param {Object|null} pageContext - Current page context from PageContextProvider.
 *   Sent alongside every message so the agent knows what the user is viewing.
 * @returns {{ messages, isLoading, error, sendMessage, resetChat }}
 */
export default function useChat(pageContext = null) {
  const [messages, setMessages] = useState(loadMessages);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Ref to avoid stale closures in async callbacks
  const messagesRef = useRef(messages);

  // Ref for pageContext so sendMessage reads the latest value at call time
  // without needing it as a dependency (avoids infinite re-render loop)
  const pageContextRef = useRef(pageContext);
  useEffect(() => { pageContextRef.current = pageContext; }, [pageContext]);

  // Keep ref in sync and persist on every change
  useEffect(() => {
    messagesRef.current = messages;
    saveMessages(messages);
  }, [messages]);

  /**
   * Send a user message to the agent and append the response.
   */
  const sendMessage = useCallback(async (text) => {
    if (!text.trim() || isLoading) return;

    const userMessage = {
      role: 'user',
      content: text.trim(),
      timestamp: new Date().toISOString()
    };

    // Append user message immediately
    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);
    setError(null);

    try {
      const data = await chatApi.sendMessage(text.trim(), pageContextRef.current);

      const assistantMessage = {
        role: 'assistant',
        content: data.response,
        timestamp: new Date().toISOString(),
        toolCalls: data.tool_calls || []
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (err) {
      // Determine a user-friendly error message
      let errorContent;
      if (err.status === 503 || err.status === 504) {
        errorContent = 'The assistant is currently unavailable. Please try again later.';
      } else if (err.message?.includes('timeout') || err.message?.includes('aborted')) {
        errorContent = 'The request timed out. Please try again.';
      } else {
        errorContent = 'Something went wrong. Please try again.';
      }

      // Show error as an assistant message so it appears in the conversation
      const errorMessage = {
        role: 'assistant',
        content: errorContent,
        timestamp: new Date().toISOString(),
        isError: true
      };

      setMessages(prev => [...prev, errorMessage]);
      setError(errorContent);
    } finally {
      setIsLoading(false);
    }
  }, [isLoading]);

  /**
   * Reset the chat — clears local messages and tells the backend to
   * clear the agent's conversation memory for this user.
   */
  const resetChat = useCallback(async () => {
    setMessages([]);
    setError(null);
    sessionStorage.removeItem(STORAGE_KEY);

    // Fire-and-forget reset to the backend — don't block UI
    try {
      await chatApi.resetChat();
    } catch {
      // Silently ignore — the user still gets a clean local slate
    }
  }, []);

  return { messages, isLoading, error, sendMessage, resetChat };
}
